import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { IonModal, IonIcon } from '@ionic/react';
import { downloadOutline } from 'ionicons/icons';
import { useState, useEffect } from 'react';

import packageJson from '../../package.json';
import UpdateImg from '../assets/appUpdate.png';

const AutoUpdater: React.FC = () => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<any>(null);

  useEffect(() => {
    checkForUpdates();

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        checkForUpdates();
      }
    });

    return () => {
      listener.then(f => f.remove());
    };
  }, []);

  const checkForUpdates = async () => {
    try {
      const info = await Device.getInfo();

      if (info.platform === 'web') {
        // Web/PWA check: Fetch meta.json with a cache-buster
        const response = await fetch(`/meta.json?t=${new Date().getTime()}`);
        if (!response.ok) return;

        const serverMeta = await response.json();
        const currentVersion = packageJson.version;

        if (serverMeta.version && serverMeta.version !== currentVersion) {
          setUpdateInfo({ availableVersion: serverMeta.version });
          setShowUpdateModal(true);
        }
      } else {
        // Native check: Use Capacitor App Update plugin
        const result = await AppUpdate.getAppUpdateInfo();
        if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
          setUpdateInfo(result);
          setShowUpdateModal(true);
        }
      }
    } catch (error: any) {
      console.error('Update check failed:', error);
    }
  };

  const updateApp = async () => {
    try {
      const info = await Device.getInfo();
      if (info.platform === 'web') {
        window.location.reload();
      } else {
        await AppUpdate.openAppStore();
      }
      setShowUpdateModal(false);
    } catch (error) {
      console.error('Failed to update app:', error);
    }
  };

  return (
    <IonModal
      isOpen={showUpdateModal}
      backdropDismiss={false}
      style={{
        '--height': 'auto',
        '--width': '90%',
        '--max-width': '380px',
        '--border-radius': '28px',
        '--box-shadow': '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f4f9ff 100%)',
          padding: '2.5rem 1.5rem 1.5rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.2rem',
        }}
      >
        <div
          style={{
            width: '130px',
            height: '130px',
            borderRadius: '35px',
            background: 'linear-gradient(135deg, #1d508d 0%, #2a6db5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 40px rgba(29, 80, 141, 0.25)',
            marginBottom: '0.5rem',
            position: 'relative',
          }}
        >
          <img src={UpdateImg} alt="Update" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>

        <div>
          <h2 style={{ margin: '0 0 10px', color: '#1a2a3e', fontSize: '24px', fontWeight: 800, letterSpacing: '-0.02em' }}>
            App Update Available
          </h2>
          <p style={{ margin: 0, color: '#5b6b8f', fontSize: '15px', lineHeight: 1.5 }}>
            A new version of Hyprism is now available{updateInfo?.updateAvailability ? ' on the Play Store' : ''}. Stay updated for the best performance and latest features!
          </p>
        </div>

        {updateInfo?.availableVersion && (
          <div
            style={{
              background: 'rgba(29, 80, 141, 0.06)',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              color: '#1d508d',
              fontWeight: 700,
              border: '1px solid rgba(29, 80, 141, 0.1)',
            }}
          >
            Version {updateInfo.availableVersion}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '12px', marginTop: '0.8rem' }}>
          <button
            onClick={updateApp}
            className="premium-update-btn"
            style={{
              background: 'linear-gradient(90deg, #1d508d 0%, #2a6db5 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '18px',
              padding: '18px',
              fontSize: '16px',
              fontWeight: 700,
              boxShadow: '0 10px 20px rgba(29, 80, 141, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              cursor: 'pointer',
              transition: 'transform 0.2s',
            }}
          >
            <IonIcon icon={downloadOutline} style={{ fontSize: '20px' }} />
            Update Now
          </button>

          <button
            onClick={() => setShowUpdateModal(false)}
            style={{
              background: 'transparent',
              color: '#8b9bb0',
              border: 'none',
              padding: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Not right now
          </button>
        </div>
      </div>
    </IonModal>
  );
};

export default AutoUpdater;
